import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useInvoices } from "../../api/invoices";
import { INVOICE_BULK_ACTION_MAX } from "../../consts";
import { BulkInvoiceActionResult, InvoiceListRow } from "../../types/invoices";

export type BulkInvoiceAction = "ksef" | "email";

interface BulkInvoiceActionContentProps {
  action: BulkInvoiceAction;
  invoices: InvoiceListRow[];
  /** Called when the backend queued at least one invoice. */
  onQueued: () => void;
  onClose: () => void;
}

// Previews the backend's own checks (ensure_submittable / the bulk-send view)
// so the dialog can say up front what will be skipped. The response, not this,
// is what reports the outcome.
const isKsefSubmittable = (invoice: InvoiceListRow) =>
  invoice.status === "I" &&
  invoice.kind !== "PRO" &&
  (invoice.ksef_status === "N" || invoice.ksef_status === "R");

const invoiceListSx = {
  maxHeight: 220,
  overflowY: "auto",
  border: "1px solid var(--dataGridBorderColor)",
  borderRadius: "var(--defaultRadius)",
};

export default function BulkInvoiceActionContent({
  action,
  invoices,
  onQueued,
  onClose,
}: BulkInvoiceActionContentProps) {
  const { t } = useTranslation();
  const { bulkSendInvoicesToKsef, bulkSendInvoices, loading } = useInvoices();
  const [includeAlreadySent, setIncludeAlreadySent] = useState(false);
  const [result, setResult] = useState<BulkInvoiceActionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isKsef = action === "ksef";
  const issued = invoices.filter((invoice) => invoice.status === "I");
  const alreadySentCount = issued.filter(
    (invoice) => invoice.sent_to_client_at
  ).length;
  const eligibleCount = isKsef
    ? invoices.filter(isKsefSubmittable).length
    : issued.length - (includeAlreadySent ? 0 : alreadySentCount);
  const tooMany = invoices.length > INVOICE_BULK_ACTION_MAX;

  const handleSubmit = async () => {
    setError(null);
    // Every selected id goes up, not just the ones previewed as eligible, so
    // the response accounts for the whole selection.
    const ids = invoices.map((invoice) => invoice.id);
    const response = isKsef
      ? await bulkSendInvoicesToKsef(ids)
      : await bulkSendInvoices(ids, includeAlreadySent);

    if (!response.success) {
      setError(
        response.errorData?.message || t("Could not queue the invoices")
      );
      return;
    }

    setResult(response.data);
    if (response.data.queued.length > 0) {
      onQueued();
    }
  };

  if (result) {
    const queuedCount = result.queued.length;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {queuedCount > 0 ? (
          <Alert severity="success">
            <Typography variant="body2">
              {isKsef
                ? t("Invoices queued for KSeF: {{count}}", { count: queuedCount })
                : t("Invoices queued for sending: {{count}}", {
                    count: queuedCount,
                  })}
            </Typography>
            <Typography variant="body2">
              {isKsef
                ? t("Their KSeF status will update on the list once KSeF responds.")
                : t("You will get a notification if any of them cannot be delivered.")}
            </Typography>
          </Alert>
        ) : (
          <Alert severity="warning">
            {t("None of the selected invoices could be queued.")}
          </Alert>
        )}

        {result.skipped.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("Skipped invoices")}
            </Typography>
            <List dense disablePadding sx={invoiceListSx}>
              {result.skipped.map((entry) => (
                <ListItem key={entry.id}>
                  <ListItemText
                    primary={entry.number || t("Draft")}
                    secondary={entry.reason}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" onClick={onClose}>
            {t("Close")}
          </Button>
        </Box>
      </Box>
    );
  }

  const skippedCount = isKsef
    ? invoices.length - eligibleCount
    : invoices.length - issued.length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {isKsef
          ? t(
              "The invoices will be submitted to KSeF in the background. An invoice filed in KSeF cannot be withdrawn, only corrected."
            )
          : t(
              "Each invoice will be emailed, with its PDF attached, to the client's invoicing address. Invoices whose client has no email address will be skipped."
            )}
      </Typography>

      <List dense disablePadding sx={invoiceListSx}>
        {invoices.map((invoice) => (
          <ListItem key={invoice.id}>
            <ListItemText
              primary={invoice.number || t("Draft")}
              secondary={invoice.buyer_name}
            />
          </ListItem>
        ))}
      </List>

      {skippedCount > 0 && (
        <Alert severity="info">
          {isKsef
            ? t(
                "Invoices that cannot be sent to KSeF will be skipped (not issued, proformas, or already in KSeF): {{count}}",
                { count: skippedCount }
              )
            : t("Invoices that are not issued will be skipped: {{count}}", {
                count: skippedCount,
              })}
        </Alert>
      )}

      {!isKsef && alreadySentCount > 0 && (
        <FormControlLabel
          control={
            <Checkbox
              checked={includeAlreadySent}
              onChange={(event) => setIncludeAlreadySent(event.target.checked)}
            />
          }
          label={t("Also resend invoices already sent to the client ({{count}})", {
            count: alreadySentCount,
          })}
        />
      )}

      {tooMany && (
        <Alert severity="error">
          {t("You can select at most {{max}} invoices at once.", {
            max: INVOICE_BULK_ACTION_MAX,
          })}
        </Alert>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button variant="text" onClick={onClose}>
          {t("Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || tooMany || eligibleCount === 0}
        >
          {loading
            ? t("Processing...")
            : isKsef
              ? t("Send to KSeF ({{count}})", { count: eligibleCount })
              : t("Send to clients ({{count}})", { count: eligibleCount })}
        </Button>
      </Box>
    </Box>
  );
}
